/* eslint-disable max-len */
import { useEffect, useRef, RefObject } from 'react';

/**
 * Custom hook to add an event listener to a specified element.
 * @param eventType The type of event to listen for
 * @param {(event: string) => void} callback - The callback function to handle the event.
 * @param {RefObject<HTMLElement> | Window} [element=window] - The target element to attach the event listener. Defaults to window.
 */
export default function useEventListener(
  eventType: string,
  callback: (event: KeyboardEvent | Event) => void,
  element: RefObject<HTMLElement> | Window = globalThis.window || undefined,
) {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const targetElement = element instanceof Window ? element : element?.current;
    if (!targetElement) return;

    const handler = (e: KeyboardEvent | Event) => callbackRef.current(e);
    targetElement.addEventListener(eventType, handler);

    // eslint-disable-next-line consistent-return
    return () => targetElement.removeEventListener(eventType, handler);
  }, [eventType, element]);
}
